import type { TFunction } from "i18next";
import {
  Bell,
  Briefcase,
  Globe,
  IdCard,
  Mail,
  Phone,
  Shield,
  UserIcon,
} from "lucide-react";

import { PATHS } from "@/routes/paths";
import type { IUser } from "@/types/(my-space)/users.interface";

import type { IProfileDetail, IProfileQuickLink } from "./types";

const MS_SETTINGS = PATHS.MY_SPACE.MS_SETTINGS_PATHS;

/**
 * Builds the account-details rows for `user`. Every row except Email is
 * conditional — the drawer shows what the profile actually has rather than
 * a grid of "—" placeholders.
 */
export const buildAccountDetails = (
  user: IUser,
  t: TFunction,
): IProfileDetail[] => {
  const employeeId = user.employmentDetails?.employeeId ?? user.employeeId;
  const designation = user.employmentDetails?.designation;

  const details: IProfileDetail[] = [];

  if (employeeId)
    details.push({
      icon: IdCard,
      label: t("company:shared.employee_id"),
      value: employeeId,
      copyable: true,
    });

  details.push({
    icon: Mail,
    label: t("common:chrome.email"),
    value: user.email,
    copyable: true,
    verified: user.isEmailVerified,
    badge: user.isEmailVerified
      ? undefined
      : { text: t("company:employees.unverified"), variant: "warning" },
  });

  if (user.phoneNumber)
    details.push({
      icon: Phone,
      label: "Phone",
      value: user.phoneNumber,
      copyable: true,
    });

  if (designation)
    details.push({
      icon: Briefcase,
      label: t("common:chrome.designation"),
      value: designation,
    });

  if (user.timeZone)
    details.push({
      icon: Globe,
      label: t("common:chrome.time_zone"),
      value: user.timeZone,
    });

  return details;
};

/**
 * Builds the personal-settings navigation rows. These mirror the `hTab`
 * routes of My Space › Settings — keep them in sync with
 * `routes/my-space/routes.settings.ts`.
 */
export const buildQuickLinks = (
  user: IUser,
  t: TFunction,
): IProfileQuickLink[] => [
  {
    icon: UserIcon,
    label: t("nav:tabs.profile_settings"),
    path: MS_SETTINGS.TAB_PROFILE,
  },
  {
    icon: Shield,
    label: t("common:chrome.security_and_password"),
    path: MS_SETTINGS.TAB_SECURITY,
    badge: user.isMfaEnabled
      ? { text: t("common:chrome.mfa_on"), variant: "success" }
      : { text: t("common:chrome.mfa_off"), variant: "warning" },
  },
  {
    icon: Bell,
    label: t("nav:modules.notifications"),
    path: MS_SETTINGS.TAB_NOTIFICATIONS,
  },
];
